import React, { useContext, useState, useEffect } from "react"
import { ThemeContext } from "styled-components"
import PropTypes from "prop-types"
import { graphql } from "gatsby"

import Reveal from "../utils/Reveal"

import Section from "../atoms/Section"
import Box from "../atoms/Box"

import ScrollCarousel from "../molecules/ScrollCarousel"
import FilteredGrid from "../molecules/FilteredGrid"
import SectionTitle from "../molecules/SectionTitle"
import ImageCard from "../molecules/ImageCard"
import CascadeGrid from "../molecules/CascadeGrid"
import SearchGrid from "../molecules/SearchGrid"

const ImageGrid = ({
  section,
  sectionTitle,
  flexGrid,
  imageCards,
  card,
  useCardFront,
  displayMode = "grid",
  imageFit,
  customImageWidth,
  customImageHeight,
  customSizeMobile,
  carouselWidth,
  carouselSpeed,
  gridType,
  cascadeGutter,
  textBackground = "solid", // enums: solid, gradient
  textVisibility = "visible", //enums: none, onHover, visible,
  alignText = "center",
  imageGridImageRatio,
  imageRatio,
  filter,
  IGFilter,
  filters,
  IGFilters,
  useSearchFilter,
  searchGrid,
  IGSearchGrid,
  searchedTerm,
  searchProps,
  filtersResetKey,
  currentColumns,
}) => {
  filter = IGFilter || filter
  filters = IGFilters || filters

  const isScroll = displayMode === "scroll"
  const isGrid = !isScroll && gridType === "grid"
  const isCascade = !isScroll && gridType === "cascade"
  const currentTheme = useContext(ThemeContext)

  /* SEARCH for ImageCard fields */
  const [localSearchTerm, setLocalSearchTerm] = useState(searchedTerm ?? "")
  useEffect(() => {
    if (typeof searchedTerm === "string") setLocalSearchTerm(searchedTerm)
  }, [searchedTerm])
  const handleSearch = e => setLocalSearchTerm(e.target.value)

  const resolvedSearchGrid = { ...(IGSearchGrid || searchGrid || {}) }
  // Always lowercase for SearchGrid alignment mapping (left|center|right)
  const alignmentValue = (
    resolvedSearchGrid?.alignSearchBar || "left"
  ).toLowerCase()

  const localSearchProps = {
    ...resolvedSearchGrid,
    useSearch: !!useSearchFilter,
    optionOneBool: resolvedSearchGrid?.optionOneBool ?? true, // Title
    optionTwoBool: resolvedSearchGrid?.optionTwoBool ?? true, // Description
    optionThreeBool: resolvedSearchGrid?.optionThreeBool ?? false,
    // map field names on the child card we search against
    optionOneBoolField: "searchTitle",
    optionTwoBoolField: "searchDescription",
  }

  // Merge external overrides if provided
  const mergedSearchProps = searchProps
    ? { ...localSearchProps, ...searchProps }
    : localSearchProps

  const plainRichText = rt => {
    if (!rt?.content) return ""
    try {
      const blocks =
        rt?.content?.blocks ||
        (rt?.content?.internal?.content
          ? JSON.parse(rt.content.internal.content)?.blocks
          : [])
      if (!Array.isArray(blocks)) return ""
      return blocks
        .map(b => b?.text || "")
        .join(" ")
        .trim()
    } catch {
      return ""
    }
  }

  const enhancedImageCards =
    imageCards?.map(ic => ({
      ...ic,
      searchTitle: ic?.title || "",
      searchDescription: plainRichText(ic?.descriptionRichText),
    })) || []

  return (
    <Section {...section}>
      {sectionTitle && (
        <Reveal>
          <SectionTitle {...sectionTitle} mb={6} maxWidth={42} />
        </Reveal>
      )}

      {/* Only show search in GRID gridType=grid (not scroll or cascade) */}
      {isGrid && mergedSearchProps?.useSearch && (
        <SearchGrid
          searchTerm={localSearchTerm}
          handleSearch={handleSearch}
          alignment={alignmentValue}
          key={`imagegrid-search-${alignmentValue}`}
          placeholder={resolvedSearchGrid?.defaultSearchBarText || "Search"}
          themeColors={{
            themes: currentTheme?.themes,
            sectionTheme: section?.theme,
          }}
          marginBottom={4}
        />
      )}

      {isScroll && (
        <Box position="relative">
          <Box
            position={"relative"}
            width={carouselWidth === "normal" ? "100%" : "100vw"}
            ml={carouselWidth === "normal" ? undefined : "-50vw"}
            left={carouselWidth === "normal" ? undefined : "49.3%"}
          >
            <ScrollCarousel
              carouselItems={imageCards}
              logoOrImage={"image"}
              customImageWidth={imageFit === "width" ? customImageWidth : null}
              customImageHeight={
                imageFit === "height" ? customImageHeight : null
              }
              customSizeMobile={customSizeMobile}
              carouselSpeed={carouselSpeed}
            />
          </Box>
        </Box>
      )}
      {isGrid && (
        <FilteredGrid
          flexGrid={flexGrid}
          filters={filters}
          filtersStyles={{
            filterLocation: filter?.filterLocation,
            alignMenuItems: filter?.alignMenuItems,
            showFilterLabel: filter?.showFilterLabel,
            layout: filter?.layout,
            filterTextDisplay: filter?.filterTextDisplay,
            dropdownCard: filter?.dropdownCard,
          }} // only sending style fields to avoid possibly overwriting filter data
          searchedTerm={
            typeof searchedTerm === "string" ? searchedTerm : localSearchTerm
          }
          searchProps={mergedSearchProps}
          filtersResetKey={filtersResetKey}
          currentColumns={currentColumns}
          card={{
            theme: section?.theme,
            bg: section?.bg,
            borderRadius: "md",
            // boxShadow: "md"
            boxShadow: "0 3px 10px rgb(0 0 0 / 30%)",
          }}
        >
          {enhancedImageCards &&
            enhancedImageCards?.map((imageCard, i) => {
              return (
                <ImageCard
                  key={i}
                  {...imageCard}
                  card={{ ...card, useCard: useCardFront }}
                  alignText={alignText}
                  columns={flexGrid?.columns}
                  textVisibility={textVisibility}
                  textBackground={textBackground}
                  imageRatio={imageGridImageRatio || imageRatio}
                  imageFieldId={`${section.sectionId}.imageCards[${i}].image`}
                />
              )
            })}
        </FilteredGrid>
      )}
      {isCascade && (
        <CascadeGrid
          children={enhancedImageCards || []}
          flexGrid={flexGrid}
          maxWidth={section?.maxWidth}
          cascadeGutter={cascadeGutter}
        />
      )}
    </Section>
  )
}

export default ImageGrid

ImageGrid.strapiProps = {
  strapi_id: PropTypes.number.isRequired,
  section: PropTypes.shape(Section.strapiProps).isRequired,
  sectionTitle: PropTypes.shape(SectionTitle.strapiProps).isRequired,
  imageCards: PropTypes.arrayOf(PropTypes.shape(ImageCard.strapiProps)),
  useCardFront: PropTypes.bool,
  alignText: PropTypes.oneOf(["left", "center", "right"]),
  textVisibility: PropTypes.oneOf(["none", "onHover", "visible"]),
  textBackground: PropTypes.oneOf(["solid", "gradient"]),
  ...FilteredGrid.strapiProps,
}

ImageGrid.propTypes = {
  ...ImageGrid.strapiProps,
  useSearchFilter: PropTypes.bool,
  searchGrid: PropTypes.object,
  IGSearchGrid: PropTypes.object,
  searchedTerm: PropTypes.string,
  searchProps: PropTypes.object,
  filtersResetKey: PropTypes.number,
  currentColumns: PropTypes.number,
}

export const query = graphql`
  fragment ImageGrid on STRAPI__COMPONENT_SECTIONS_IMAGE_GRID {
    strapi_id
    IMAGEGRIDSection: section {
      ...Section
    }
    sectionTitle {
      ...SectionTitle
    }
    flexGrid {
      ...FlexGrid
    }
    card {
      ...Card
    }
    imageCards {
      ...ImageCard
    }
    IGFilters: filters {
      ...Filter
    }
    IGFilter: filter {
      alignMenuItems
      filterLocation
      showFilterLabel
      layout
      filterTextDisplay
      dropdownCard {
        ...Card
      }
    }
    useCardFront
    alignText
    textVisibility
    displayMode
    imageFit
    customImageWidth
    customImageHeight
    customSizeMobile
    carouselWidth
    carouselSpeed
    gridType
    cascadeGutter
    textBackground
    imageGridImageRatio: imageRatio
    useSearchFilter
    IGSearchGrid: searchGrid {
      ...SearchGrid
    }
  }
`
